// Settings › My notifications: a text to my own cell when a customer replies, browser push,
// and a test button. Per person, not per workspace.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPhone, normalizeE164 } from '@bt/core';
import { api, type NotificationPrefs } from '../../api';
import { Loading, ErrorBox, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { currentPushSubscription, pushSupported, subscribePush, unsubscribePush } from '../../lib/push';

export function NotificationsSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['notifications-me'], queryFn: api.notifications.me });
  const [number, setNumber] = useState('');
  const [sms, setSms] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [hasBrowserSub, setHasBrowserSub] = useState<boolean | null>(null);

  useEffect(() => {
    if (q.data) {
      setNumber(q.data.notify_sms_number ? formatPhone(q.data.notify_sms_number) : '');
      setSms(q.data.notify_sms);
    }
  }, [q.data]);
  useEffect(() => {
    currentPushSubscription().then((s) => setHasBrowserSub(!!s));
  }, []);

  const e164 = number.trim() ? normalizeE164(number) : null;
  const numberBad = !!number.trim() && !e164;

  const saveSms = useMutation({
    mutationFn: () => api.notifications.update({ notify_sms_number: e164 ?? null, notify_sms: sms && !!e164 }),
    onSuccess: (d) => {
      qc.setQueryData(['notifications-me'], d);
      toast({ text: 'Saved.', tone: 'ok' });
    },
  });

  const setPush = async (on: boolean, prefs: NotificationPrefs) => {
    setPushBusy(true);
    setPushError(null);
    try {
      if (on) {
        if (!prefs.vapid_public_key) throw new Error('Browser notifications are not set up on the server yet (VAPID keys). Run scripts/gen-vapid.ts and add the keys to .env.');
        const sub = await subscribePush(prefs.vapid_public_key);
        await api.notifications.subscribe(sub);
        const d = await api.notifications.update({ notify_push: true });
        qc.setQueryData(['notifications-me'], d);
        setHasBrowserSub(true);
        toast({ text: 'Browser notifications are on for this browser.', tone: 'ok' });
      } else {
        const endpoint = await unsubscribePush();
        if (endpoint) await api.notifications.unsubscribe(endpoint).catch(() => {});
        const d = await api.notifications.update({ notify_push: false });
        qc.setQueryData(['notifications-me'], d);
        setHasBrowserSub(false);
      }
    } catch (e) {
      setPushError((e as Error).message);
    } finally {
      setPushBusy(false);
    }
  };

  const test = useMutation({
    mutationFn: () => api.notifications.test(),
    onSuccess: (r) => {
      const parts: string[] = [];
      if (r.sms) parts.push('a text to your cell');
      if (r.push) parts.push('a browser notification');
      toast({ text: parts.length ? `Sent ${parts.join(' and ')}.${r.note ? ` ${r.note}` : ''}` : r.note ?? 'Nothing to send: turn on a channel first.', tone: parts.length ? 'ok' : 'default' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} retry={() => q.refetch()} />;
  const p = q.data;
  const supported = pushSupported();
  const dirty = (p.notify_sms_number ? formatPhone(p.notify_sms_number) : '') !== number || p.notify_sms !== sms;

  return (
    <div className="card">
      <h2>My notifications</h2>
      <p className="muted small">When a customer replies, the app can reach you on your own cell and in this browser. STOP replies never trigger an alert.</p>

      <label className="field">
        <span>My cell number</span>
        <input className="input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="(239) 555-0123" style={{ maxWidth: 260 }} />
        {numberBad && <div className="tiny danger-text">Not a full number yet.</div>}
        <div className="field-help">The alert comes from the same texting line the customer wrote to.</div>
      </label>
      <div className="switch">
        <input type="checkbox" id="notify-sms" checked={sms} onChange={(e) => setSms(e.target.checked)} disabled={!e164} />
        <label htmlFor="notify-sms" className="grow">
          <strong>Text me when a customer replies</strong>
          <span className="muted">"SweetText: Maria replied: …" At most one text per conversation every 10 minutes.{!e164 ? ' Enter your cell number first.' : ''}</span>
        </label>
      </div>
      <InlineError error={saveSms.error} />
      <button className="btn primary small" style={{ marginTop: 8 }} disabled={!dirty || numberBad || saveSms.isPending} onClick={() => saveSms.mutate()}>
        {saveSms.isPending ? 'Saving' : 'Save'}
      </button>

      <div className="switch" style={{ marginTop: 16, borderTop: '1px solid var(--border)' }}>
        <input type="checkbox" id="notify-push" checked={p.notify_push && hasBrowserSub !== false} onChange={(e) => void setPush(e.target.checked, p)} disabled={pushBusy || !supported} />
        <label htmlFor="notify-push" className="grow">
          <strong>Browser notifications</strong>
          <span className="muted">
            {!supported
              ? 'This browser does not support push notifications.'
              : p.notify_push && hasBrowserSub === false
                ? 'On for your account, but not on this browser yet. Tick to turn it on here.'
                : 'A notification on this computer even when the tab is closed. Clicking it opens the thread.'}
            {!p.vapid_public_key && supported ? ' Not available until the server has VAPID keys.' : ''}
          </span>
        </label>
        {pushBusy && <span className="spinner" aria-hidden="true" />}
      </div>
      {pushError && <div className="tiny danger-text">{pushError}</div>}

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        <button className="btn" disabled={test.isPending || (!p.notify_sms && !p.notify_push)} onClick={() => test.mutate()} title={!p.notify_sms && !p.notify_push ? 'Turn on a channel first' : undefined}>
          {test.isPending ? 'Sending' : 'Send me a test'}
        </button>
      </div>
    </div>
  );
}
